/**
 * lib/hours.ts — parse the business `hours` and work out closing times,
 * so the booking form can warn when an appointment runs past closing.
 */
import type { BusinessHours } from '@/data/site';

const DAY_INDEX: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

function dayIndices(dayStr: string): number[] {
  const s = dayStr.toLowerCase();
  const range = s.split(/[–-]/).map((x) => x.trim());
  if (range.length === 2 && DAY_INDEX[range[0]] != null && DAY_INDEX[range[1]] != null) {
    const a = DAY_INDEX[range[0]];
    const b = DAY_INDEX[range[1]];
    const out: number[] = [];
    for (let i = a; ; i = (i + 1) % 7) { out.push(i); if (i === b) break; }
    return out;
  }
  return s.split(/[,/]/).map((x) => DAY_INDEX[x.trim()]).filter((i) => i != null);
}

export function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function fmtMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export interface DayHours { open: string; close: string; day: string; }

/** Hours for the weekday of an ISO date string (YYYY-MM-DD), or null. */
export function hoursForDate(hours: BusinessHours[], dateStr: string): DayHours | null {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const wd = d.getDay();
  for (const h of hours) {
    if (dayIndices(h.day).includes(wd)) {
      const [open, close] = h.time.split(/[–-]/).map((x) => x.trim());
      if (open && close) return { open, close, day: h.day };
    }
  }
  return null;
}

/**
 * Returns a warning string if a booking starting at `time` (HH:MM) and lasting
 * `durationMin` would finish after closing on `dateStr`; otherwise null.
 */
export function closingWarning(
  hours: BusinessHours[], dateStr: string, time: string, durationMin: number,
): string | null {
  if (!dateStr || !time || !durationMin) return null;
  const dh = hoursForDate(hours, dateStr);
  if (!dh) return null;
  const start = minutesOf(time);
  const end = start + durationMin;
  const open = minutesOf(dh.open);
  const close = minutesOf(dh.close);
  if (start < open) return `We open at ${dh.open} on that day — please choose a later start time.`;
  if (end > close) {
    return `Heads up: about ${durationMin} min of treatments from ${time} would finish around ${fmtMinutes(end)}, after we close at ${dh.close}. Please pick an earlier time or fewer treatments.`;
  }
  return null;
}
